import { z } from "zod";
import { BusinessGoalV1Schema, ExecutionModeV1Schema, RiskPreferenceV1Schema } from "./ai-policy";
import { CHANGE_HISTORY_PAGE_SIZES } from "./campaign-change-history";

/**
 * 托管组操作日志 — what happened to a Campaign through AI Managed Groups:
 * joining / leaving / moving between groups, its AI switch, and the
 * group-level changes (policy, pause, archive) made while it was a member.
 * Rendered into sentences client-side so both locales stay in the UI layer.
 */
export const managedGroupHistoryActionSchema = z.enum([
  "added",
  "removed",
  "moved",
  "ai_on",
  "ai_off",
  "group_updated",
  "group_activated",
  "group_paused",
  "group_archived",
]);
export type ManagedGroupHistoryAction = z.infer<typeof managedGroupHistoryActionSchema>;

/** The policy fields a sentence names; absent when the event did not carry them. */
export const managedGroupHistoryPolicySchema = z.object({
  targetAcos: z.number().optional(),
  businessGoal: BusinessGoalV1Schema.optional(),
  riskPreference: RiskPreferenceV1Schema.optional(),
  executionMode: ExecutionModeV1Schema.optional(),
});
export type ManagedGroupHistoryPolicy = z.infer<typeof managedGroupHistoryPolicySchema>;

const groupRefSchema = z.object({ id: z.string().uuid(), name: z.string() });

export const managedGroupHistoryItemSchema = z.object({
  id: z.string().uuid(),
  occurredAt: z.string().datetime({ offset: true }),
  action: managedGroupHistoryActionSchema,
  /** The group, named as it was at the time of the event. */
  group: groupRefSchema,
  /** `moved`: the group the campaign left. */
  fromGroup: groupRefSchema.nullable(),
  /** `added`: the policy the campaign joined under. */
  policy: managedGroupHistoryPolicySchema.nullable(),
  /** `group_updated`: what changed, old → new. */
  changes: z.array(
    z.discriminatedUnion("field", [
      z.object({ field: z.literal("name"), from: z.string(), to: z.string() }),
      z.object({ field: z.literal("targetAcos"), from: z.number(), to: z.number() }),
      z.object({
        field: z.literal("businessGoal"),
        from: BusinessGoalV1Schema,
        to: BusinessGoalV1Schema,
      }),
      z.object({
        field: z.literal("riskPreference"),
        from: RiskPreferenceV1Schema,
        to: RiskPreferenceV1Schema,
      }),
      z.object({
        field: z.literal("executionMode"),
        from: ExecutionModeV1Schema,
        to: ExecutionModeV1Schema,
      }),
    ]),
  ),
  /** Null for a system action (no user), shown as "System". */
  operatorEmail: z.string().nullable(),
});
export type ManagedGroupHistoryItem = z.infer<typeof managedGroupHistoryItemSchema>;
export type ManagedGroupHistoryChange = ManagedGroupHistoryItem["changes"][number];

export const campaignManagedGroupHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .refine(
      (n) => (CHANGE_HISTORY_PAGE_SIZES as readonly number[]).includes(n),
      "Invalid page size",
    )
    .default(200),
});
export type CampaignManagedGroupHistoryQuery = z.infer<
  typeof campaignManagedGroupHistoryQuerySchema
>;

export const campaignManagedGroupHistoryResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ items: z.array(managedGroupHistoryItemSchema) }),
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
    hasMore: z.boolean(),
  }),
});
export type CampaignManagedGroupHistoryResponse = z.infer<
  typeof campaignManagedGroupHistoryResponseSchema
>;
